import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Target, Trash, PencilSimple, Check, Plus, Minus } from '@phosphor-icons/react';
import { gameSystems, toBaseUnits, spendCoins, addCoins, getTotalInBaseUnits, getSymbol } from '../data/gameSystems';
import { useTranslation } from '../hooks/useTranslation';
import { useApp } from '../context/AppContext';
import { Progress } from './ui/progress';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { ScrollArea } from './ui/scroll-area';
import { toast } from 'sonner';

export const GoalsList = ({ goals, wallet, systemId, onEdit, onDelete }) => {
  const { t, lang } = useTranslation();
  const { dispatch, selectedCharacter } = useApp();
  const system = gameSystems[systemId];
  const [depositAmounts, setDepositAmounts] = useState({});
  const [withdrawAmounts, setWithdrawAmounts] = useState({});
  
  const handleDeposit = (goal) => {
    const amount = parseInt(depositAmounts[goal.id] || 0, 10);
    if (!amount || amount <= 0) return;
    
    const currency = system.currencies.find(c => c.id === goal.currencyId);
    const amountInBase = amount * currency.valueInBase;
    const walletTotalBase = getTotalInBaseUnits(wallet, systemId);
    
    if (amountInBase > walletTotalBase) {
      toast.error(t('insufficientFunds'));
      return;
    }
    
    const result = spendCoins(wallet, amount, goal.currencyId, systemId);
    if (!result.success) {
      toast.error(t('insufficientFunds'));
      return;
    }
    
    dispatch({
      type: 'UPDATE_WALLET',
      payload: { characterId: selectedCharacter.id, wallet: result.wallet }
    });
    
    dispatch({
      type: 'DEPOSIT_TO_GOAL',
      payload: { characterId: selectedCharacter.id, goalId: goal.id, amount, currencyId: goal.currencyId }
    });
    
    dispatch({
      type: 'ADD_TRANSACTION',
      payload: {
        characterId: selectedCharacter.id,
        transaction: {
          type: 'goalDeposit',
          amount,
          currencyId: goal.currencyId,
          note: `→ ${goal.name}`,
        }
      }
    });
    
    setDepositAmounts({ ...depositAmounts, [goal.id]: '' });
    toast.success(t('transactionSuccess'));
  };
  
  const handleWithdraw = (goal) => {
    const amount = parseInt(withdrawAmounts[goal.id] || 0, 10);
    if (!amount || amount <= 0) return;
    
    if (amount > (goal.savedAmount || 0)) {
      toast.error(t('insufficientFunds'));
      return;
    }
    
    const newWallet = addCoins(wallet, amount, goal.currencyId);
    
    dispatch({
      type: 'UPDATE_WALLET',
      payload: { characterId: selectedCharacter.id, wallet: newWallet }
    });
    
    dispatch({
      type: 'WITHDRAW_FROM_GOAL',
      payload: { characterId: selectedCharacter.id, goalId: goal.id, amount }
    });
    
    dispatch({
      type: 'ADD_TRANSACTION',
      payload: {
        characterId: selectedCharacter.id,
        transaction: {
          type: 'goalWithdraw',
          amount,
          currencyId: goal.currencyId,
          note: `← ${goal.name}`,
        }
      }
    });
    
    setWithdrawAmounts({ ...withdrawAmounts, [goal.id]: '' });
    toast.success(t('transactionSuccess'));
  };
  
  if (!goals || goals.length === 0) {
    return (
      <div className="py-12 text-center">
        <Target size={40} weight="thin" className="mx-auto mb-3 text-[var(--text-muted)]" />
        <p className="text-[var(--text-muted)]">{t('noGoals')}</p>
        <p className="text-sm text-[var(--text-muted)] mt-1">{t('createFirstGoal')}</p>
      </div>
    );
  }
  
  return (
    <ScrollArea className="h-[360px]">
      <div className="space-y-3 pr-2">
        {goals.map((goal, index) => {
          const currency = system.currencies.find(c => c.id === goal.currencyId);
          const savedAmount = goal.savedAmount || 0;
          const progress = Math.min((savedAmount / goal.targetAmount) * 100, 100);
          const isReached = progress >= 100;
          
          return (
            <motion.div
              key={goal.id}
              data-testid={`goal-${goal.id}`}
              className={`p-4 rounded-2xl ${isReached ? 'bg-green-500/10 border border-green-500/20' : 'bg-[var(--background)]'}`}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05 }}
            >
              {/* Header */}
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                    isReached ? 'bg-green-500/20' : 'bg-[var(--primary)]/10'
                  }`}>
                    {isReached ? (
                      <Check size={20} weight="bold" className="text-green-500" />
                    ) : (
                      <Target size={20} weight="fill" className="text-[var(--primary)]" />
                    )}
                  </div>
                  <div>
                    <h4 className={`text-base font-semibold ${isReached ? 'text-green-500' : 'text-[var(--text)]'}`}>
                      {goal.name}
                    </h4>
                    <p className="text-sm text-[var(--text-muted)]">
                      {savedAmount} / {goal.targetAmount} {currency ? getSymbol(currency, lang) : ''}
                    </p>
                  </div>
                </div>
                
                <div className="flex gap-1">
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => onEdit(goal)}
                    className="h-8 w-8 text-[var(--text-muted)]"
                    data-testid={`edit-goal-${goal.id}`}
                  >
                    <PencilSimple size={16} />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => onDelete(goal.id)}
                    className="h-8 w-8 text-[var(--text-muted)] hover:text-red-500"
                    data-testid={`delete-goal-${goal.id}`}
                  >
                    <Trash size={16} />
                  </Button>
                </div>
              </div>
              
              {/* Progress */}
              <div className="mb-3">
                <Progress 
                  value={progress} 
                  className={`h-2 ${isReached ? '[&>div]:bg-green-500' : '[&>div]:bg-[var(--primary)]'}`}
                />
                <p className={`text-xs mt-1 ${isReached ? 'text-green-500' : 'text-[var(--text-muted)]'}`}>
                  {isReached ? t('goalReached') : `${progress.toFixed(0)}%`}
                </p>
              </div>
              
              {/* Actions */}
              <div className="flex gap-2">
                <div className="flex-1 flex gap-1">
                  <Input
                    type="number"
                    min="1"
                    placeholder="0"
                    value={depositAmounts[goal.id] || ''}
                    onChange={(e) => setDepositAmounts({ ...depositAmounts, [goal.id]: e.target.value })}
                    className="h-9 text-sm bg-[var(--surface)] border-[var(--border)] text-[var(--text)] flex-1"
                    data-testid={`deposit-input-${goal.id}`}
                  />
                  <Button
                    size="sm"
                    onClick={() => handleDeposit(goal)}
                    className="h-9 px-3 bg-green-500 hover:bg-green-600 text-white"
                    data-testid={`deposit-btn-${goal.id}`}
                  >
                    <Plus size={16} />
                  </Button>
                </div>
                
                {savedAmount > 0 && (
                  <div className="flex-1 flex gap-1">
                    <Input
                      type="number"
                      min="1"
                      max={savedAmount}
                      placeholder="0"
                      value={withdrawAmounts[goal.id] || ''}
                      onChange={(e) => setWithdrawAmounts({ ...withdrawAmounts, [goal.id]: e.target.value })}
                      className="h-9 text-sm bg-[var(--surface)] border-[var(--border)] text-[var(--text)] flex-1"
                      data-testid={`withdraw-input-${goal.id}`}
                    />
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleWithdraw(goal)}
                      className="h-9 px-3 border-[var(--border)] text-[var(--text)]"
                      data-testid={`withdraw-btn-${goal.id}`}
                    >
                      <Minus size={16} />
                    </Button>
                  </div>
                )}
              </div>
            </motion.div>
          );
        })}
      </div>
    </ScrollArea>
  );
};
